import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Check, X, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/app/AppShell';
import { useApprovals, useDecideApproval, useCancelApproval, type Approval } from '@/lib/approvals';
import { useCurrentUser } from '@/lib/auth';
import { useMyCompany } from '@/lib/companies';
import { confirm, promptText } from '@/components/ui/ConfirmDialog';

type StatusFilter = 'pending' | 'approved' | 'rejected' | 'canceled' | 'all';

export function ApprovalsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'mine' | 'all'>('mine');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const items = useApprovals({
    mine: tab === 'mine' ? true : undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });
  const user = useCurrentUser();
  const company = useMyCompany();
  const decide = useDecideApproval();
  const cancel = useCancelApproval();

  const isJpCompany = (company.data?.hqCountryCode ?? '').toUpperCase() === 'JP';

  const STATUS_FILTERS: StatusFilter[] = ['pending', 'approved', 'rejected', 'canceled', 'all'];

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-5xl mx-auto space-y-6">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('approvals.eyebrow', { defaultValue: 'Approvals' })}</p>
          <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight">{t('approvals.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('approvals.subtitle')}</p>
        </header>

        {/* Mine / All inbox tabs */}
        <div className="inline-flex rounded-xl border border-border bg-card p-1">
          <Tab active={tab === 'mine'} onClick={() => setTab('mine')}>{t('approvals.myInbox')}</Tab>
          <Tab active={tab === 'all'} onClick={() => setTab('all')}>{t('approvals.all')}</Tab>
        </div>

        {/* Status filter tabs */}
        <div className="inline-flex rounded-xl border border-border bg-card p-1 flex-wrap gap-0.5">
          {STATUS_FILTERS.map((s) => (
            <Tab key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)}>
              {t(`approvals.statusFilter.${s}`, { defaultValue: s.charAt(0).toUpperCase() + s.slice(1) })}
            </Tab>
          ))}
        </div>

        <section className="space-y-3">
          {items.isLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {items.data?.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-12">{t('approvals.noPending')}</p>
          )}
          {(items.data ?? []).map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.3) }}
            >
              <ApprovalCard
                a={a}
                currentUserId={user.data?.id}
                isJpCompany={isJpCompany}
                onDecide={(action, reason) => decide.mutate({ id: a.id, action, reason })}
                onCancel={() => cancel.mutate(a.id)}
              />
            </motion.div>
          ))}
        </section>
      </div>
    </AppShell>
  );
}

function Tab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${
        active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

const STATUS_META: Record<string, { Icon: React.ComponentType<{ className?: string }>; className: string }> = {
  pending:  { Icon: Clock,        className: 'text-amber-600' },
  approved: { Icon: CheckCircle2, className: 'text-emerald-600' },
  rejected: { Icon: XCircle,      className: 'text-rose-600' },
  canceled: { Icon: XCircle,      className: 'text-slate-500' },
};

/** Map a targetKind to its list/detail route. Returns null when no route exists. */
function targetKindRoute(kind: string, id: string): string | null {
  switch (kind) {
    case 'document':
      return '/app/documents';
    case 'payroll_run':
    case 'payroll':
      return '/app/payroll';
    case 'employee':
      return `/app/people/${id}`;
    case 'nencho':
    case 'cycle':
      return '/app/year-end';
    default:
      return null;
  }
}

function ApprovalCard({
  a, currentUserId, isJpCompany, onDecide, onCancel,
}: {
  a: Approval;
  currentUserId?: string;
  isJpCompany: boolean;
  onDecide: (action: 'approved' | 'rejected', reason?: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const isCurrentSigner = a.status === 'pending' && a.requiredSigners[a.currentStep] === currentUserId;
  const isOwner = a.status === 'pending' && a.createdBy !== null && a.createdBy === currentUserId;
  const meta = STATUS_META[a.status] ?? STATUS_META.pending;
  const Icon = meta.Icon;
  const route = targetKindRoute(a.targetKind, a.targetId);

  const onApprove = async () => {
    const ok = await confirm({
      title: t('approvals.confirmApproveTitle', { defaultValue: 'Approve this request?' }),
      description: t('approvals.confirmApproveBody', { defaultValue: 'Your decision is final for this step. It will be audit-logged with your IP and user-agent.' }),
      confirmLabel: t('approvals.approve'),
    });
    if (ok) onDecide('approved');
  };

  const onReject = async () => {
    const reason = await promptText({
      title: t('approvals.rejectTitle', { defaultValue: 'Reject — reason?' }),
      description: t('approvals.rejectBody', { defaultValue: 'A short reason helps the requester understand and resubmit.' }),
      inputLabel: t('common.reason', { defaultValue: 'Reason' }),
      placeholder: t('approvals.rejectPlaceholder', { defaultValue: 'Salary band exceeded the manager budget' }),
      minLength: 4,
      tone: 'destructive',
      confirmLabel: t('approvals.reject'),
    });
    if (reason) onDecide('rejected', reason);
  };

  const onCancelClick = async () => {
    const ok = await confirm({
      title: t('approvals.confirmCancelTitle', { defaultValue: 'Cancel this request?' }),
      description: t('approvals.confirmCancelBody', { defaultValue: 'This will withdraw the approval request. Signers will no longer be able to act on it.' }),
      confirmLabel: t('approvals.cancel', { defaultValue: 'Cancel request' }),
      tone: 'destructive',
    });
    if (ok) onCancel();
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          {route ? (
            <Link to={route} className="text-sm font-semibold hover:text-primary transition-colors">
              {t(`approvals.targetKindLabels.${a.targetKind}`, { defaultValue: a.targetKind })}
              {' '}
              <span className="num text-[11px] text-muted-foreground">{a.targetId.slice(0, 12)}…</span>
            </Link>
          ) : (
            <>
              <p className="text-sm font-semibold">{t(`approvals.targetKindLabels.${a.targetKind}`, { defaultValue: a.targetKind })}</p>
              <p className="num text-[11px] text-muted-foreground">{a.targetId.slice(0, 12)}…</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {t('approvals.stepX', { current: a.currentStep + 1, total: a.requiredSigners.length })}
          </span>
          <span className={`inline-flex items-center gap-1 text-xs font-semibold ${meta.className}`}>
            <Icon className="h-3.5 w-3.5" />
            {t(`status.${a.status}`)}
          </span>
          {/* JP companies get the hanko stamp visualisation on approved
              approvals — peddlum/fluxez parity for the JP-deep wedge. */}
          {isJpCompany && a.status === 'approved' && (
            <span className="hanko text-base shrink-0" aria-label="hanko stamp" title="hanko">承認</span>
          )}
          {isJpCompany && a.status === 'rejected' && (
            <span className="hanko text-base shrink-0" aria-label="hanko stamp" title="hanko" style={{ borderColor: '#94a3b8', color: '#64748b' }}>却下</span>
          )}
        </div>
      </div>

      {a.history.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground mb-1.5">{t('approvals.history')}</p>
          <ul className="space-y-0.5">
            {a.history.map((h, i) => (
              <li key={i} className="text-[11px] text-foreground/70">
                <span className="num">{new Date(h.at).toLocaleString()}</span>
                {' · '}{h.action}{' · step '}{h.step + 1}
                {h.reason ? <span className="italic"> · "{h.reason}"</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(isCurrentSigner || isOwner) && (
        <div className="mt-4 flex gap-2 justify-end">
          {isOwner && (
            <button
              onClick={onCancelClick}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs font-semibold hover:bg-slate-200 dark:hover:bg-slate-700 transition"
            >
              <X className="h-3.5 w-3.5" />
              {t('approvals.cancelRequest', { defaultValue: 'Cancel request' })}
            </button>
          )}
          {isCurrentSigner && (
            <>
              <button
                onClick={onReject}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-500/10 text-rose-600 text-xs font-semibold hover:bg-rose-500 hover:text-white transition"
              >
                <X className="h-3.5 w-3.5" />
                {t('approvals.reject')}
              </button>
              <button
                onClick={onApprove}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500/10 text-emerald-700 text-xs font-semibold hover:bg-emerald-600 hover:text-white transition"
              >
                <Check className="h-3.5 w-3.5" />
                {t('approvals.approve')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
